import { Box, Button, FileButton, Image, LoadingOverlay, UnstyledButton } from "@dehub/theme";
import { CheckIcon, TrashIcon, UploadIcon } from "@phosphor-icons/react";
import React from "react";
import { useTranslation } from "react-i18next";
import { v4 as uuidv4 } from "uuid";
import { MediaApi } from "@/apis";
import { message } from "@/utils/message";
import classes from "./multi-image-upload.module.css";

export type IFile = {
  uid: string;
  uploading: boolean;
  url?: string;
  selected: boolean;
  file: Blob;
  thumbnail?: string;
};

type Props = Readonly<{
  onChange?: (files: IFile[]) => void;
  max?: number;
  initialData?: Array<{ url: string; isMain?: boolean; thumbnail?: string }>;
}>;

export function MultiImageUpload({ onChange, max, initialData }: Props) {
  const { t } = useTranslation("common");
  const resetRef = React.useRef<() => void>(null);

  const [files, setFiles] = React.useState<Record<string, IFile>>(() => {
    if (!initialData || !Array.isArray(initialData)) return {};

    return initialData.reduce((acc: Record<string, IFile>, curr, index: number) => {
      const uid = `${index}`;
      acc[uid] = {
        uid,
        uploading: false,
        selected: Boolean(curr.isMain),
        url: curr.url,
        file: new Blob(),
        thumbnail: curr.thumbnail,
      };
      return acc;
    }, {});
  });

  const updateUploadedFile = (uid: string, url: string, thumbnail: string) => {
    setFiles((prevFiles) => {
      const updatedFile = {
        ...prevFiles[uid],
        uploading: false,
        url,
        uid,
        thumbnail,
      };

      const updatedFiles = {
        ...prevFiles,
        [uid]: updatedFile,
      };

      onChange?.(Object.values(updatedFiles));
      return updatedFiles;
    });
  };

  const onFileUpload = (blobs: Blob[]) => {
    const uploads = blobs.map((blob, index) => {
      const uid = uuidv4();

      uploadFile(blob, uid);

      return {
        uid,
        file: blob,
        selected: index === 0 && Object.keys(files).length === 0,
        uploading: true,
      };
    });

    // Keep the current main image: clearing it left a list with no selected image at all.
    const changes = {
      ...files,
      ...uploads.reduce((acc, cur) => ({ ...acc, [cur.uid]: cur }), {}),
    };

    setFiles(changes);
    onChange?.(Object.values(changes));
    resetRef.current?.();
  };

  const uploadFile = async (blob: Blob, uid: string) => {
    try {
      const form = new FormData();
      form.append("file", blob);

      const res = await MediaApi.uploadImage(form);
      updateUploadedFile(uid, res.url, res.thumbnail);
    } catch (err: unknown) {
      const e = err as { message?: string };
      message.error(e?.message ?? t("upload.uploadError"));
    }
  };

  const onRemove = (uid: string) => {
    const remaining = Object.keys(files).reduce<Record<string, IFile>>((accumulator, iterator) => {
      if (uid === iterator) return accumulator;
      return { ...accumulator, [iterator]: files[iterator] };
    }, {});
    // Removing the selected image would leave none selected: the first remaining one takes over.
    const firstKey = Object.keys(remaining)[0];
    const changes =
      files[uid]?.selected && firstKey && !Object.values(remaining).some((file) => file.selected)
        ? { ...remaining, [firstKey]: { ...remaining[firstKey], selected: true } }
        : remaining;

    setFiles(changes);

    resetRef.current?.();

    if (onChange) onChange(Object.values(changes));
  };

  const onSelect = (uid: string) => {
    const changes = Object.keys(files).reduce((accumulator, iterator) => {
      return {
        ...accumulator,
        [iterator]: {
          ...files[iterator],
          selected: uid === iterator,
        },
      };
    }, {});

    setFiles(changes);

    if (onChange) onChange(Object.values(changes));
  };

  return (
    <Box className={classes.multiImageUpload}>
      {Object.keys(files).map((key) => {
        const file = files[key];
        return (
          <Box className={classes.imageBox} data-selected={file.selected || undefined} key={key}>
            <Image className={classes.image} width={80} height={80} src={file.thumbnail ?? file.url ?? URL.createObjectURL(file.file)} alt="" />
            <LoadingOverlay visible={file.uploading} opacity={0.3} loaderProps={{ size: "sm" }} />
            {!file.uploading && (
              <Box className={classes.actionBox}>
                <UnstyledButton className={classes.actionButton} onClick={() => onSelect(file.uid)}>
                  <CheckIcon size={20} className={classes.iconCheck} />
                </UnstyledButton>
                <UnstyledButton className={classes.actionButton} onClick={() => onRemove(file.uid)}>
                  <TrashIcon size={20} className={classes.iconTrash} />
                </UnstyledButton>
              </Box>
            )}
          </Box>
        );
      })}

      <FileButton disabled={max ? Object.keys(files).length === max : false} resetRef={resetRef} onChange={onFileUpload} accept="image/png,image/jpeg" multiple>
        {(props) => (
          <Button disabled={max ? Object.keys(files).length === max : false} variant="light" className={classes.uploadButton} {...props}>
            <UploadIcon size={20} />
          </Button>
        )}
      </FileButton>
    </Box>
  );
}
